import { CheckCircle2, Clock3, ImageIcon, ListOrdered } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { OkdocScenarioSection, OkdocScenarioSectionComponent, OkdocScenarioSectionEntry } from '@/features/okdoc/types';

interface ScenarioSectionContentProps {
  section: OkdocScenarioSection;
  mode?: 'public' | 'preview';
}

export function normalizeRecommendationEntries(section?: OkdocScenarioSection): OkdocScenarioSectionEntry[] {
  if (!section) {
    return [];
  }

  if (section.entries && section.entries.length > 0) {
    return section.entries.filter((entry) => !!(entry.title || entry.description || entry.meta));
  }

  return (section.items || [])
    .map((item, index) => ({
      id: `${section.id || 'section'}-item-${index + 1}`,
      description: item,
    }))
    .filter((entry) => !!entry.description);
}

export function normalizeRecommendationComponent(section?: OkdocScenarioSection): OkdocScenarioSectionComponent | string {
  if (section?.component) {
    return section.component;
  }
  return 'checklist';
}

export function getRecommendationComponentLabel(component?: string): string {
  switch (component) {
    case 'numbered_list':
      return '1-2-3 список';
    case 'timeline':
      return 'Таймлайн';
    case 'checklist':
    default:
      return 'Чек-лист';
  }
}

export function ScenarioSectionContent({ section, mode = 'public' }: ScenarioSectionContentProps) {
  const entries = normalizeRecommendationEntries(section);
  const component = normalizeRecommendationComponent(section);
  const isPreview = mode === 'preview';

  const imageCardClass = isPreview
    ? 'rounded-xl border border-neutral-200 bg-neutral-50'
    : 'rounded-[24px] border border-slate-200 bg-slate-50/70';

  const itemCardClass = isPreview
    ? 'rounded-xl border border-neutral-200 bg-neutral-50 px-3 py-3'
    : 'rounded-2xl border border-slate-100 bg-slate-50/70 px-4 py-3';

  const numberClass = isPreview
    ? 'bg-blue-100 text-blue-700'
    : 'bg-slate-900 text-white';

  const timelineDotClass = isPreview ? 'bg-blue-500 ring-neutral-50' : 'bg-slate-900 ring-white';
  const timelineLineClass = isPreview ? 'bg-neutral-200' : 'bg-slate-200';

  return (
    <div className="mt-4 space-y-4">
      {section.image?.url && (
        <div className={cn('overflow-hidden', imageCardClass)}>
          <img
            src={section.image.url}
            alt={section.image.alt || section.title || 'Recommendation illustration'}
            className="h-48 w-full object-cover"
          />
          {section.image.caption && (
            <div className="flex items-start gap-2 px-4 py-3 text-sm text-slate-600">
              <ImageIcon className="mt-0.5 h-4 w-4 shrink-0" />
              <span>{section.image.caption}</span>
            </div>
          )}
        </div>
      )}

      {entries.length > 0 && component === 'timeline' && (
        <div className="relative ml-2 space-y-4 pl-6">
          <div className={cn('absolute bottom-2 left-[11px] top-2 w-px', timelineLineClass)} />
          {entries.map((entry, index) => (
            <div key={entry.id || `timeline-${index}`} className="relative">
              <div className={cn('absolute left-[-14px] top-2 h-3 w-3 rounded-full ring-4', timelineDotClass)} />
              <div className={cn('rounded-2xl px-4 py-3', itemCardClass)}>
                {(entry.meta || entry.title) && (
                  <div className="flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-900">
                    <Clock3 className="h-4 w-4 text-slate-500" />
                    <span>{entry.meta || entry.title}</span>
                  </div>
                )}
                {entry.description && (
                  <div className={cn('text-sm leading-6 text-slate-700', entry.meta || entry.title ? 'mt-2' : '')}>
                    {entry.description}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {entries.length > 0 && component === 'numbered_list' && (
        <div className="space-y-3">
          {entries.map((entry, index) => (
            <div key={entry.id || `numbered-${index}`} className={cn('flex gap-3', itemCardClass)}>
              <div className={cn('flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-sm font-semibold', numberClass)}>
                {index + 1}
              </div>
              <div className="min-w-0 flex-1">
                {entry.title && <div className="text-sm font-semibold text-slate-900">{entry.title}</div>}
                {entry.description && (
                  <div className={cn('text-sm leading-6 text-slate-700', entry.title ? 'mt-1' : '')}>
                    {entry.description}
                  </div>
                )}
                {entry.meta && <div className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-500">{entry.meta}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {entries.length > 0 && component !== 'timeline' && component !== 'numbered_list' && (
        <div className="space-y-3">
          {entries.map((entry, index) => (
            <div key={entry.id || `checklist-${index}`} className={cn('flex gap-3', itemCardClass)}>
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-500" />
              <div className="min-w-0 flex-1">
                {entry.title && <div className="text-sm font-semibold text-slate-900">{entry.title}</div>}
                {entry.description && (
                  <div className={cn('text-sm leading-6 text-slate-700', entry.title ? 'mt-1' : '')}>
                    {entry.description}
                  </div>
                )}
                {entry.meta && <div className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-500">{entry.meta}</div>}
              </div>
            </div>
          ))}
        </div>
      )}

      {entries.length === 0 && (
        <div className={cn('rounded-2xl border border-dashed px-4 py-3 text-sm text-slate-500', itemCardClass)}>
          Содержимое секции пока не заполнено.
        </div>
      )}
    </div>
  );
}

export function RecommendationComponentGlyph({ component }: { component?: string }) {
  switch (component) {
    case 'numbered_list':
      return <ListOrdered className="h-5 w-5" />;
    case 'timeline':
      return <Clock3 className="h-5 w-5" />;
    case 'checklist':
    default:
      return <CheckCircle2 className="h-5 w-5" />;
  }
}
